'use client';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import { useMutation } from '@parkchirp/api-client';
import { FC, SyntheticEvent, useCallback, useState } from 'react';

import { DELETE_BANK_ACCOUNTS, DELETE_CREDIT_CARDS } from '../../queries';
import { PaymentOptionProps } from '../../types';
import { BankAccountForm } from './BankAccountForm';
import { CreditCardForm } from './creditCardForm';

// Code copied over from admin dashboard for re-use
export const PaymentOptions: FC<PaymentOptionProps> = ({
	useAch,
	useCreditCard,
	editPaymentOptions,
	cognitoSub,
	profileData,
	subscriptionId,
	setShowSnackbar,
	migratePayorComponent,
	gpiFormConfig,
	tenantSubscriptionId,
	refetchPaymentMethods,
	paymentProviderId,
	forTenant,
}) => {
	const [checkedCreditCards, setCheckedCreditCards] = useState<string[]>([]);
	const [checkedBankAccounts, setCheckedBankAccounts] = useState<string[]>([]);

	const cards = profileData?.gpiCards;
	const bankAccounts = profileData?.gpiBankAccounts;

	const [deleteCreditCard, { loading: deleteCardLoading }] = useMutation(DELETE_CREDIT_CARDS, {
		onCompleted: () => {
			setShowSnackbar({
				message: 'Successfully deleted.',
				severity: 'success',
				autoHideDuration: 3000,
				onClose: () => {
					setShowSnackbar(null);
				},
			});
			setCheckedCreditCards((prev) => prev.filter((token) => !checkedCreditCards.includes(token)));
		},
	});

	const handleCreditCardDelete = useCallback(
		async (event: SyntheticEvent) => {
			event.preventDefault();

			try {
				await deleteCreditCard({
					variables: {
						cognitoSub,
						cardToken: checkedCreditCards,
						subscriptionId,
						tenantSubscriptionId,
						paymentProviderId,
					},
				});

				refetchPaymentMethods();
			} catch (error: any) {
				setShowSnackbar({
					message: error?.message,
					severity: 'error',
					autoHideDuration: 3000,
					onClose: () => {
						setShowSnackbar(null);
					},
				});
			}
		},
		[
			deleteCreditCard,
			setShowSnackbar,
			cognitoSub,
			checkedCreditCards,
			subscriptionId,
			tenantSubscriptionId,
			refetchPaymentMethods,
			paymentProviderId,
		],
	);

	const [deleteBankAccounts, { loading: deleteAccountLoading }] = useMutation(DELETE_BANK_ACCOUNTS, {
		onCompleted: () => {
			setShowSnackbar({
				message: 'Successfully deleted.',
				severity: 'success',
				autoHideDuration: 3000,
				onClose: () => {
					setShowSnackbar(null);
				},
			});
			setCheckedBankAccounts((prev) => prev.filter((token) => !checkedBankAccounts.includes(token)));
		},
	});

	const handleBankAccountDelete = useCallback(
		async (event: SyntheticEvent) => {
			event.preventDefault();

			try {
				await deleteBankAccounts({
					variables: {
						cognitoSub,
						accountTokens: checkedBankAccounts,
						subscriptionId,
						tenantSubscriptionId,
						paymentProviderId,
					},
				});

				refetchPaymentMethods();
			} catch (error: any) {
				setShowSnackbar({
					message: error.message,
					severity: 'error',
					autoHideDuration: 3000,
					onClose: () => {
						setShowSnackbar(null);
					},
				});
			}
		},
		[
			deleteBankAccounts,
			setShowSnackbar,
			cognitoSub,
			checkedBankAccounts,
			subscriptionId,
			tenantSubscriptionId,
			refetchPaymentMethods,
			paymentProviderId,
		],
	);

	const enableEdit = editPaymentOptions !== 'DISPLAY_AND_VIEW_ONLY';
	if (!useCreditCard && !useAch) {
		return (
			<Alert severity='info' sx={{ mt: 1 }}>
				Online payments are not available for this subscription. Please contact an admin for help with payment
				options.
			</Alert>
		);
	}
	return (
		<Box sx={{ pt: 2, pb: 3, pl: 5, pr: 5 }}>
			{useCreditCard && (
				<>
					<Box marginBottom={3}>
						<Typography variant='h6' style={{ fontWeight: 'bold', marginBottom: 1 }}>
							Manage Credit Cards
						</Typography>
						{!cognitoSub && migratePayorComponent ? <>{migratePayorComponent}</> : null}
						<Box
							sx={{
								mt: 1,
								mb: 1,
								display: 'flex',
								flexDirection: 'column',
								justifyContent: 'flex-start',
								width: '30%',
							}}
						>
							{cards?.map((card, index) => {
								const expMonth = card.exp_month || card.expMonth;
								const expYear = card.exp_year || card.expYear;
								return (
									<FormControlLabel
										key={card.token}
										control={
											<Checkbox
												size='small'
												onChange={(e) => {
													if (e.target.checked) {
														setCheckedCreditCards((prev) => [...prev, card.token]);
													} else {
														setCheckedCreditCards((prev) =>
															prev.filter((token) => token !== card.token),
														);
													}
												}}
												data-cy={`credit-card-option-checkbox-${index}`}
											/>
										}
										label={
											<Typography variant='body2'>
												{`${card.type} ending in ${card.last4}, ${expMonth}/${expYear}`}
											</Typography>
										}
									/>
								);
							})}
						</Box>
						{enableEdit && (
							<Box sx={{ mt: 2, mb: 4 }}>
								<Button
									variant='outlined'
									size='small'
									color='error'
									disabled={checkedCreditCards.length === 0}
									onClick={handleCreditCardDelete}
									data-cy='delete-credit-card-button'
								>
									{deleteCardLoading ? (
										<CircularProgress size={20} sx={{ color: '#d14a41' }} />
									) : (
										'Delete Selected Card'
									)}
								</Button>
							</Box>
						)}
					</Box>
					{enableEdit && (
						<Box marginBottom={5}>
							<Typography variant='h6' style={{ fontWeight: 'bold' }}>
								Add Credit Card
							</Typography>
							<CreditCardForm
								paymentProviderId={paymentProviderId}
								subscriptionId={subscriptionId}
								tenantSubscriptionId={tenantSubscriptionId}
								cognitoSub={cognitoSub}
								gpiFormConfig={gpiFormConfig}
								setShowSnackbar={setShowSnackbar}
								refetchPaymentMethods={refetchPaymentMethods}
							/>
						</Box>
					)}
				</>
			)}
			{useAch && (
				<>
					<Box marginTop={10} marginBottom={3}>
						<Typography variant='h6' style={{ fontWeight: 'bold', marginBottom: 1 }}>
							Manage Bank Accounts
						</Typography>
						<Box
							sx={{
								mt: 1,
								mb: 1,
								display: 'flex',
								flexDirection: 'column',
								justifyContent: 'flex-start',
								width: '30%',
							}}
						>
							{bankAccounts?.map((account, index) => (
								<FormControlLabel
									key={account.token}
									control={
										<Checkbox
											size='small'
											onChange={(e) => {
												if (e.target.checked) {
													setCheckedBankAccounts((prev) => [...prev, account.token]);
												} else {
													setCheckedBankAccounts((prev) =>
														prev.filter((token) => token !== account.token),
													);
												}
											}}
											data-cy={`bank-account-option-checkbox-${index}`}
										/>
									}
									label={
										<Typography variant='body2'>
											{`${account.accountType} account ending in ${account.last4}`}
										</Typography>
									}
								/>
							))}
						</Box>
						{enableEdit && (
							<Box sx={{ mt: 2, mb: 4 }}>
								<Button
									variant='outlined'
									size='small'
									color='error'
									disabled={checkedBankAccounts.length === 0}
									onClick={handleBankAccountDelete}
									data-cy='delete-bank-account-button'
								>
									{deleteAccountLoading ? (
										<CircularProgress size={20} sx={{ color: '#d14a41' }} />
									) : (
										'Delete Selected Account'
									)}
								</Button>
							</Box>
						)}
					</Box>
					{enableEdit && (
						<Box marginBottom={3}>
							{!forTenant && (
								<Alert severity='info' sx={{ mb: 2 }} data-cy='nacha-admin-compliance-notice'>
									By adding a bank account and processing an ACH payment, you agree to comply with
									NACHA&apos;s Operating Rules.
								</Alert>
							)}
							<Typography variant='h6' style={{ fontWeight: 'bold' }}>
								Add Bank Account
							</Typography>
							<BankAccountForm
								cognitoSub={cognitoSub}
								setShowSnackbar={setShowSnackbar}
								tenantSubscriptionId={tenantSubscriptionId}
								refetchPaymentMethods={refetchPaymentMethods}
								paymentProviderId={paymentProviderId}
							/>
						</Box>
					)}
				</>
			)}
		</Box>
	);
};
